import React, { useState, useEffect, useCallback } from 'react';
import {
  getMerchantInfo,
  listDevices,
  createDevice,
  deleteDevice,
  setDefaultDevice,
  simulateDeviceInteraction,
  SIMULATE_DEVICE_PRESETS,
  isSandboxRainforestId,
} from '../services/paymentsService';

const DeviceManagementTab = ({ practiceId }) => {
  const [merchantInfo, setMerchantInfo] = useState(null);
  const [merchantNotFound, setMerchantNotFound] = useState(false);
  const [devices, setDevices] = useState([]);
  const [defaultDeviceId, setDefaultDeviceId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  const [showAddForm, setShowAddForm] = useState(false);
  const [registrationCode, setRegistrationCode] = useState('');
  const [deviceName, setDeviceName] = useState('');
  const [creating, setCreating] = useState(false);
  
  const [deletingId, setDeletingId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [settingDefaultId, setSettingDefaultId] = useState(null);
  const [simulatingId, setSimulatingId] = useState(null);

  const isSandbox = !!merchantInfo?.demo;

  const loadMerchantData = useCallback(async () => {
    try {
      setError('');
      const merchantData = await getMerchantInfo(practiceId);
      
      if (merchantData?.merchant_status === 'not_found') {
        setMerchantNotFound(true);
        setMerchantInfo(null);
      } else if (merchantData?.merchant_status === 'found') {
        setMerchantInfo(merchantData?.merchant || null);
        setMerchantNotFound(false);
      } else {
        setMerchantInfo(merchantData?.merchant || null);
        setMerchantNotFound(!merchantData?.merchant);
      }
    } catch (err) {
      setError('Failed to load merchant data. Please check your connection.');
      setMerchantInfo(null);
      setMerchantNotFound(false);
    }
  }, [practiceId]);

  const loadDevices = useCallback(async (refresh = false) => {
    try {
      setDevicesLoading(true);
      const result = await listDevices(practiceId, refresh);
      
      if (result.success) {
        const deviceList = Array.isArray(result.devices) ? result.devices : [];
        setDevices(deviceList);
        setDefaultDeviceId(result.default_device_id || null);
        
        if (result.error_refreshing) {
          setError(`Using cached data. Refresh failed: ${result.error_refreshing}`);
        }
      } else {
        if (!result.error?.includes('No merchant')) {
          setError(result.error || 'Failed to load devices');
        }
        setDevices([]);
      }
    } catch (err) {
      setError('Failed to load devices. Please check your connection.');
      setDevices([]);
    } finally {
      setDevicesLoading(false);
    }
  }, [practiceId]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await loadMerchantData();
      setLoading(false);
    };

    if (practiceId) {
      loadData();
    }
  }, [practiceId, loadMerchantData]);

  useEffect(() => {
    if (merchantInfo && !merchantNotFound) {
      loadDevices(true);
    }
  }, [merchantInfo, merchantNotFound, loadDevices]);

  const handleAddDevice = async (e) => {
    e.preventDefault();
    
    if (!registrationCode.trim()) {
      setError('Registration code is required');
      return;
    }
    
    if (!deviceName.trim()) {
      setError('Device name is required');
      return;
    }
    
    if (deviceName.length > 20) {
      setError('Device name must be 20 characters or less');
      return;
    }
    
    try {
      setCreating(true);
      setError('');
      
      const result = await createDevice(practiceId, registrationCode.trim(), deviceName.trim());
      
      if (result.success) {
        setSuccessMessage('Device registered successfully!');
        setShowAddForm(false);
        setRegistrationCode('');
        setDeviceName('');
        await loadDevices(true);
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(result.error || result.details || 'Failed to register device');
      }
    } catch (err) {
      setError('Failed to register device. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteDevice = async (deviceRegistrationId) => {
    try {
      setDeletingId(deviceRegistrationId);
      setError('');
      
      const result = await deleteDevice(practiceId, deviceRegistrationId);
      
      if (result.success) {
        setSuccessMessage('Device deleted successfully!');
        setShowDeleteConfirm(null);
        await loadDevices(true);
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(result.error || 'Failed to delete device');
      }
    } catch (err) {
      setError('Failed to delete device. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleSimulate = async (deviceRegistrationId, presetId) => {
    const preset = SIMULATE_DEVICE_PRESETS.find((p) => p.id === presetId);
    if (!preset) return;

    try {
      setSimulatingId(deviceRegistrationId);
      setError('');

      const result = await simulateDeviceInteraction(
        practiceId,
        deviceRegistrationId,
        preset.event,
        preset.card,
      );

      if (result.success) {
        setSuccessMessage(`Simulated: ${preset.label}`);
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(result.error || 'Failed to simulate device interaction');
      }
    } catch (err) {
      setError('Failed to simulate device interaction. Present a payin/payment-method on the device first.');
    } finally {
      setSimulatingId(null);
    }
  };

  const handleSetDefault = async (deviceRegistrationId) => {
    try {
      setSettingDefaultId(deviceRegistrationId);
      setError('');
      
      const result = await setDefaultDevice(practiceId, deviceRegistrationId);
      
      if (result.success) {
        setDefaultDeviceId(deviceRegistrationId);
        setSuccessMessage('Default device updated!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(result.error || 'Failed to set default device');
      }
    } catch (err) {
      setError('Failed to set default device. Please try again.');
    } finally {
      setSettingDefaultId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div>
      {error && (
        <p style={{ color: '#b42318', marginBottom: '20px' }}>⚠️ {error}</p>
      )}
      
      {successMessage && (
        <p style={{ color: '#1d6b40', marginBottom: '20px' }}>✓ {successMessage}</p>
      )}

      <div style={{ 
        backgroundColor: 'white', 
        padding: '20px', 
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '20px'
      }}>
        {merchantInfo && !merchantNotFound ? (
          <div>
            <div style={{ 
              textAlign: 'center',
              marginBottom: '20px'
            }}>
              <h3 style={{ margin: '0 0 15px 0', color: '#1f5a3d' }}>
                Registered Devices
                {isSandbox && (
                  <span
                    title="Merchant is configured for the NewtonPay sandbox. The Simulate Tap dropdown is available on each device row."
                    style={{
                      marginLeft: '10px',
                      padding: '2px 8px',
                      backgroundColor: '#fdf3e3',
                      color: '#a8520c',
                      borderRadius: '10px',
                      fontSize: '0.7rem',
                      fontWeight: 'bold',
                      verticalAlign: 'middle',
                    }}
                  >
                    SANDBOX
                  </span>
                )}
              </h3>
              <button
                onClick={() => setShowAddForm(!showAddForm)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: showAddForm ? '#b42318' : '#1d6b40',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.9rem'
                }}
              >
                {showAddForm ? '✕ Cancel' : '+ Add Device'}
              </button>
            </div>

            {showAddForm && (
              <div style={{ 
                backgroundColor: '#f7f8f7', 
                padding: '20px', 
                borderRadius: '8px',
                marginBottom: '20px',
                border: '1px solid #e3e7e4'
              }}>
                <h4 style={{ margin: '0 0 15px 0', color: '#3c4540' }}>
                  Register New Device
                </h4>
                <form onSubmit={handleAddDevice}>
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#3c4540' }}>
                      Registration Code *
                    </label>
                    <input
                      type="text"
                      value={registrationCode}
                      onChange={(e) => setRegistrationCode(e.target.value)}
                      placeholder="Enter code shown on device"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        fontSize: '1rem',
                        border: '1px solid #d6dbd7',
                        borderRadius: '6px',
                        boxSizing: 'border-box'
                      }}
                    />
                    <small style={{ color: '#6b716d', display: 'block', marginTop: '4px' }}>
                      Code displayed on the device screen when not registered.
                    </small>
                  </div>
                  
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#3c4540' }}>
                      Device Name * (max 20 chars)
                    </label>
                    <input
                      type="text"
                      value={deviceName}
                      onChange={(e) => setDeviceName(e.target.value)}
                      placeholder="e.g., Front Desk Terminal"
                      maxLength={20}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        fontSize: '1rem',
                        border: '1px solid #d6dbd7',
                        borderRadius: '6px',
                        boxSizing: 'border-box'
                      }}
                    />
                    <small style={{ color: '#6b716d', display: 'block', marginTop: '4px' }}>
                      {deviceName.length}/20 - Name shown on the device.
                    </small>
                  </div>
                  
                  <button
                    type="submit"
                    disabled={creating}
                    style={{
                      padding: '10px 24px',
                      backgroundColor: creating ? '#6b716d' : '#1f5a3d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: creating ? 'not-allowed' : 'pointer',
                      fontSize: '1rem',
                      fontWeight: 'bold'
                    }}
                  >
                    {creating ? 'Registering...' : 'Register Device'}
                  </button>
                </form>
              </div>
            )}

            {devicesLoading && devices.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', backgroundColor: '#f7f8f7', borderRadius: '8px' }}>
                <p style={{ color: '#6b716d' }}>Loading devices...</p>
              </div>
            ) : devices.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '40px',
                backgroundColor: '#f7f8f7',
                borderRadius: '8px'
              }}>
                <p style={{ margin: 0, color: '#6b716d', fontSize: '1.1rem' }}>
                  📱 No devices registered yet
                </p>
                <p style={{ margin: '10px 0 0 0', color: '#6b716d' }}>
                  Click "Add Device" to register your first payment terminal.
                </p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.95rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f7f8f7' }}>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e3e7e4' }}>Device Name</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e3e7e4' }}>Make / Model</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e3e7e4' }}>Serial Number</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e3e7e4' }}>Created</th>
                      <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #e3e7e4' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {devices.map((device) => {
                      const isDefault = device.device_registration_id === defaultDeviceId;
                      return (
                        <tr key={device.device_registration_id} style={{ borderBottom: '1px solid #e3e7e4' }}>
                          <td style={{ padding: '12px', textAlign: 'left' }}>
                            <strong>{device.device_name || 'Unnamed'}</strong>
                            {isDefault && (
                              <span style={{
                                marginLeft: '8px',
                                padding: '2px 8px',
                                backgroundColor: '#1d6b40',
                                color: 'white',
                                borderRadius: '10px',
                                fontSize: '0.7rem',
                                fontWeight: 'bold'
                              }}>
                                DEFAULT
                              </span>
                            )}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'left', textTransform: 'capitalize' }}>
                            {device.make || '-'} {device.model || ''}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'left', fontFamily: 'monospace', fontSize: '0.85rem', color: '#6b716d' }}>
                            {device.serial_number || '-'}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'left', color: '#6b716d', fontSize: '0.9rem' }}>
                            {formatDate(device.created_at)}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            {showDeleteConfirm === device.device_registration_id ? (
                              <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                <button
                                  onClick={() => handleDeleteDevice(device.device_registration_id)}
                                  disabled={deletingId === device.device_registration_id}
                                  style={{
                                    padding: '6px 12px',
                                    backgroundColor: '#b42318',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: deletingId ? 'not-allowed' : 'pointer',
                                    fontSize: '0.85rem'
                                  }}
                                >
                                  {deletingId === device.device_registration_id ? '...' : 'Confirm'}
                                </button>
                                <button
                                  onClick={() => setShowDeleteConfirm(null)}
                                  style={{
                                    padding: '6px 12px',
                                    backgroundColor: '#6b716d',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '0.85rem'
                                  }}
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                                {isSandboxRainforestId(device.device_registration_id) && (
                                  <select
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      e.target.value = '';
                                      if (value) handleSimulate(device.device_registration_id, value);
                                    }}
                                    disabled={simulatingId === device.device_registration_id}
                                    title="Sandbox-only: trigger a virtual tap/swipe so the presented payin or payment method moves out of PRESENTING."
                                    style={{
                                      padding: '6px 8px',
                                      backgroundColor: simulatingId === device.device_registration_id ? '#eef0ee' : '#fdf3e3',
                                      color: '#a8520c',
                                      border: '1px solid #f3d9ae',
                                      borderRadius: '4px',
                                      cursor: simulatingId === device.device_registration_id ? 'not-allowed' : 'pointer',
                                      fontSize: '0.85rem',
                                    }}
                                  >
                                    <option value="">
                                      {simulatingId === device.device_registration_id ? 'Simulating...' : 'Simulate Tap ▾'}
                                    </option>
                                    {SIMULATE_DEVICE_PRESETS.map((preset) => (
                                      <option key={preset.id} value={preset.id}>
                                        {preset.label}
                                      </option>
                                    ))}
                                  </select>
                                )}
                                {!isDefault && (
                                  <button
                                    onClick={() => handleSetDefault(device.device_registration_id)}
                                    disabled={settingDefaultId === device.device_registration_id}
                                    style={{
                                      padding: '6px 12px',
                                      backgroundColor: settingDefaultId === device.device_registration_id ? '#cdd3cf' : '#1f5a3d',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      cursor: settingDefaultId === device.device_registration_id ? 'not-allowed' : 'pointer',
                                      fontSize: '0.85rem'
                                    }}
                                  >
                                    {settingDefaultId === device.device_registration_id ? '...' : 'Set Default'}
                                  </button>
                                )}
                                <button
                                  onClick={() => setShowDeleteConfirm(device.device_registration_id)}
                                  style={{
                                    padding: '6px 12px',
                                    backgroundColor: 'transparent',
                                    color: '#b42318',
                                    border: '1px solid #b42318',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '0.85rem'
                                  }}
                                >
                                  Delete
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : merchantNotFound ? (
          <div style={{ 
            backgroundColor: '#fdf3e3', 
            padding: '15px', 
            borderRadius: '4px'
          }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#a8520c' }}>
              ⚠️ No Merchant Setup Found
            </h4>
            <p style={{ margin: 0, fontSize: '0.9rem', color: '#a8520c' }}>
              This practice doesn't have payment processing setup yet. Please set up your merchant account 
              in the Merchant tab first to manage devices.
            </p>
          </div>
        ) : (
          <p style={{ color: '#6b716d', fontStyle: 'italic' }}>
            Loading merchant information...
          </p>
        )}
      </div>
    </div>
  );
};

export default DeviceManagementTab;
